import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { favoritesApi, BACKEND_URL } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import FavoriteButton from '../components/favorites/FavoriteButton';
import { Heart, ChevronRight, BookOpen, Layers } from 'lucide-react';
import { format } from 'date-fns';

const LIMIT = 24;

// content_type -> the 2 display buckets a favorite can fall into (see UserFavorite's
// docstring in backend/db/models.py for what the 3 content_type values themselves mean).
const isArticleType = (contentType) => contentType === 'article' || contentType === 'page_content_article';

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'article', label: 'Articles' },
  { value: 'how_it_works_topic', label: 'How It Works Topics' },
];

const resolveThumbnail = (path) => {
  if (!path) return null;
  return path.startsWith('/uploads/') ? `${BACKEND_URL}${path}` : path;
};

const formatDate = (dateStr) => {
  try {
    return format(new Date(dateStr), 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
};

const FavoriteCard = ({ item, onRemoved }) => (
  <Link to={item.url} data-testid={`favorite-card-${item.content_id}`}>
    <Card className="technical-card h-full overflow-hidden relative">
      <FavoriteButton
        contentType={item.content_type}
        contentId={item.content_id}
        initialFavorited
        title={item.title}
        className="absolute top-3 right-3 z-10 bg-white/80 hover:bg-white"
        onChange={(favorited) => {
          if (!favorited) onRemoved(item);
        }}
      />
      {item.thumbnail && (
        <img src={resolveThumbnail(item.thumbnail)} alt="" className="w-full h-40 object-cover" />
      )}
      <CardContent className="p-6 flex flex-col h-full">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Badge variant="outline" className="w-fit font-mono text-xs uppercase tracking-wider">
            {isArticleType(item.content_type) ? (
              <BookOpen className="h-3 w-3 mr-1" />
            ) : (
              <Layers className="h-3 w-3 mr-1" />
            )}
            {isArticleType(item.content_type) ? 'Article' : 'How It Works Topic'}
          </Badge>
          {item.section && (
            <Badge variant="outline" className="w-fit font-mono text-xs uppercase tracking-wider">
              {item.section}
            </Badge>
          )}
        </div>
        <h3 className="font-chivo text-lg font-bold mb-2 line-clamp-2 flex-1">{item.title}</h3>
        <div className="flex items-center justify-between pt-4 border-t border-structure">
          <span className="text-xs text-muted-foreground">Favorited {formatDate(item.created_at)}</span>
          <ChevronRight className="h-4 w-4 text-autoxium-blue shrink-0" />
        </div>
      </CardContent>
    </Card>
  </Link>
);

const EmptyState = () => (
  <div className="text-center py-16" data-testid="no-favorites-message">
    <Heart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
    <h3 className="font-chivo text-xl font-bold uppercase mb-2">No Favorites Yet</h3>
    <p className="text-muted-foreground mb-6">
      You haven't favorited anything yet — browse Articles or How Does It Work topics to get started.
    </p>
    <div className="flex items-center justify-center gap-3">
      <Link to="/articles">
        <Button className="bg-autoxium-blue hover:bg-autoxium-blue/90 text-white font-bold uppercase tracking-wider">
          Browse Articles
        </Button>
      </Link>
      <Link to="/how-it-works">
        <Button variant="outline" className="font-bold uppercase tracking-wider">
          How Does It Work?
        </Button>
      </Link>
    </div>
  </div>
);

const DashboardPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  const fetchFavorites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await favoritesApi.list(skip, LIMIT);
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error('Error fetching favorites:', err);
    } finally {
      setLoading(false);
    }
  }, [skip]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const handleRemoved = (removedItem) => {
    setItems((prev) => prev.filter((i) => i.content_id !== removedItem.content_id));
    setTotal((prev) => Math.max(0, prev - 1));
  };

  const visibleItems = items.filter((item) => {
    if (tab === 'all') return true;
    if (tab === 'article') return isArticleType(item.content_type);
    return !isArticleType(item.content_type);
  });

  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + LIMIT, total);

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      {/* Hero */}
      <section className="bg-chassis text-white py-16 md:py-20">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <p className="font-mono text-xs tracking-widest text-autoxium-blue uppercase mb-4">
            {user?.first_name ? `Welcome back, ${user.first_name}` : 'Your Account'}
          </p>
          <h1 className="font-chivo text-4xl md:text-6xl font-black uppercase tracking-tighter mb-4">
            Dashboard
          </h1>
          <p className="text-lg text-white/70 max-w-xl leading-relaxed">
            My Favorites — Articles and How Does It Work topics you've saved for later.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList data-testid="favorites-filter-tabs">
                {TABS.map((t) => (
                  <TabsTrigger key={t.value} value={t.value} data-testid={`favorites-tab-${t.value}`}>
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            {total > 0 && (
              <span className="font-mono text-sm text-muted-foreground">
                {from}-{to} of {total} favorite{total !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-loading">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="technical-card">
                  <CardContent className="p-6">
                    <Skeleton className="h-4 w-20 mb-4" />
                    <Skeleton className="h-6 w-full mb-2" />
                    <Skeleton className="h-4 w-full mb-4" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : total === 0 ? (
            <EmptyState />
          ) : visibleItems.length === 0 ? (
            <div className="text-center py-16" data-testid="no-favorites-in-tab-message">
              <p className="text-muted-foreground">No favorites in this category on the current page.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-grid">
              {visibleItems.map((item) => (
                <FavoriteCard key={`${item.content_type}-${item.content_id}`} item={item} onRemoved={handleRemoved} />
              ))}
            </div>
          )}

          {total > LIMIT && (
            <div className="flex items-center justify-end gap-2 mt-8">
              <Button
                variant="outline"
                size="sm"
                disabled={skip === 0}
                onClick={() => setSkip((s) => Math.max(0, s - LIMIT))}
                data-testid="favorites-prev-page-btn"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={skip + LIMIT >= total}
                onClick={() => setSkip((s) => s + LIMIT)}
                data-testid="favorites-next-page-btn"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
